import { useEffect, useState } from "react";
import api from "../../../../services/api";

import "react-toastify/dist/ReactToastify.css";
import { displayDateTime } from "../../../../utils/masks";
import { TablePage } from "../../../../containers";
import ProtocolAddEdit from "../protocol-add-edit/ProtocolAddEdit";
import { authenticationService } from "../../../../services/auth.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye, faPencil } from "@fortawesome/free-solid-svg-icons";
import File from "../../../../models/File";
import ViewerModal from "../../../../components/viewer";
import { toast } from "react-toastify";
import toastOptions from "../../../../utils/toastOptions";

interface ProtocolListDTO {
  id: number;
  name: string;
  description: string;
  createdDate: Date;
  createdBy: string;
  file?: File;
}

export default function ProtocolList() {
  const [refresh, setRefresh] = useState<boolean>();
  const [total, setTotal] = useState(0);
  const [termSearch, setTermSearch] = useState<string>("");
  const [delayedSearchTerm, setDelayedSearchTerm] = useState('');
  const [add, setAdd] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [isReadOnly, setReadOnly] = useState<boolean>(true);
  const [fileUrl, setFileUrl] = useState<string>('');

  const columns = [
    {
      name: "Descrição",
      sortable: true,
      grow: 2,
      cell: (row: ProtocolListDTO) => <span>{row.description}</span>
    },
    {
      name: "Entregue por",
      sortable: true,
      cell: (row: ProtocolListDTO) => <span>{row.name}</span>
    },
    {
      name: "Criado Por",
      sortable: true,
      cell: (row: ProtocolListDTO) => <span>{row.createdBy} recebido em {displayDateTime(row.createdDate)}</span>
    },
    {
      name: 'Ações',
      button: true,
      sortable: false,
      omit: isReadOnly,
      cell: (row: ProtocolListDTO) => (
          <div className='uk-flex uk-flex-between'>
              {
                row.file && (<>
                  <a href="#" 
                    uk-tooltip="title: Visualizar Documento"
                    className='uk-margin-small-right' 
                    onClick={(ev) => { ev.preventDefault(); handleView(row.file) }}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </a>
                  <a href="#" uk-tooltip="title: Baixar Documento"
                    className='uk-margin-small-right' 
                    onClick={(ev) => { ev.preventDefault(); handleDowload(row.file) }}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </a>
                </>)
              }
          </div>                    
      )
    }
  ];

  useEffect(() => {
    setReadOnly(!authenticationService.canChange());
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);  

  const fetch = async (page: number, limit: number) => {
    try {
      setTotal(0);

      let params = {
        page: page,
        limit: limit
      } as any;

      if (termSearch) {
        params = { ...params, name: termSearch?.toUpperCase() };
      }

      const response = await api.get('protocols', {
        params: params
      });

      setTotal(response.data.total);

      setPageNo(page);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
      return { data: [], total: 0 };
    }
  };

  const handleSearch = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
      setRefresh(!refresh);
    }
  };

  const handleOptions = () => {
    return (
      <div className="uk-search uk-search-default uk-width-1-2@m">
        <span
          className="uk-search-icon-flip"
          uk-search-icon="true"
        ></span>
        <input
          className="uk-search-input uk-input uk-form-small"
          type="search"
          placeholder="Pesquise por nome, descrição ou nº do protocolo"
          style={{ textTransform: "uppercase" }}
          onChange={(ev) => {
            handleSearch(ev.target.value);
          }}
        />
      </div>
    )
  }
  
  const handleAddProtocol = () => {
    setAdd(true)
  }

  const afterHandleSubmit = () => {
    afterCloseModal();
    setRefresh(!refresh);
  }

  const afterCloseModal = () => {
    setAdd(false);
  }

  const handleDowload = async (file?: File) => {
    if (!file) return;

    const link = document.createElement('a');
    link.href = file.content;
    link.download = file.name;
    window.URL.revokeObjectURL(link.href);
    link.click();        
  }

  const handleView = async (file?: File) => {
    if (!file) return;

    setFileUrl(file.content);
  }

  return (<>
    <TablePage 
      title="Protocolos Realizados"
      columns={columns}
      fetch={fetch}
      refresh={refresh}
      backUrl="/secretaria"
      showSubHeader
      totalRecords={total}
      labelTotalRecords={`${total} ${total === 1 ? "Protocolo" : "Protocolos"}`}
      options={handleOptions()}
      showAddButton={!isReadOnly}
      handleAdd={handleAddProtocol}
    />
    <ProtocolAddEdit add={add} afterHandleSubmit={afterHandleSubmit} afterCloseModal={afterCloseModal} />
    <ViewerModal isOpen={fileUrl.length > 0} fileUrl={fileUrl} onClose={()=> setFileUrl('')} />
  </>
  )
}
